// Contenu e-learning de la formation certifiante (parcours AVOCAT / ARBITRE).
// Aligné sur PROGRAMME_FORMATION_IA_GSF_ARBITRAL_V2_CORRIGE.md et sur les
// curricula backend (formation_canonical.py). QCM des modules notés : 5
// questions, bonne réponse = scoreMax/5 ; le score atteint le seuil backend.

export interface QcmQuestion {
  question: string;
  options: string[];
  correct: number;
}

export interface ModuleContenu {
  id: string;
  titre: string;
  objectifs: string[];
  points: string[];
  scoreMax: number | null;
  seuil: number | null;
  qcm: QcmQuestion[];
}

// --- Modules communs aux deux parcours -------------------------------------
const ONBOARDING: ModuleContenu = {
  id: "onboarding",
  titre: "Module 0 — Onboarding",
  objectifs: [
    "Comprendre la vision GSF : l'IA au service de la justice, jamais à sa place.",
    "Signer la charte éthique.",
  ],
  points: [
    "L'avocat = conseil ; l'arbitre = juridiction (séparation stricte).",
    "L'IA assiste, elle ne décide pas.",
  ],
  scoreMax: null,
  seuil: null,
  qcm: [],
};

const RGPD: ModuleContenu = {
  id: "rgpd",
  titre: "Module 1 — Fondamentaux RGPD & confidentialité",
  objectifs: [
    "Maîtriser les données sensibles en arbitrage OHADA.",
    "Comprendre l'AIPD, les transferts internationaux et le hachage orphelin.",
  ],
  points: [
    "AIPD obligatoire (Art. 35.3.a) pour un système à haut risque.",
    "Transferts : Mistral (UE, nul), Claude (US, DPF+CCT), Qwen (CN, SCC+ZK).",
    "Effacement (Art. 17) : hachage orphelin — contenu supprimé, empreinte conservée.",
  ],
  scoreMax: 15,
  seuil: 12,
  qcm: [
    {
      question: "Pourquoi une AIPD est-elle obligatoire ici ?",
      options: [
        "Parce que le système est à haut risque (Art. 35.3.a RGPD)",
        "Parce que c'est optionnel",
        "Uniquement si le client le demande",
      ],
      correct: 0,
    },
    {
      question: "Que signifie « effacement » via hachage orphelin ?",
      options: [
        "On supprime tout, y compris le hash",
        "On supprime le contenu mais on conserve l'empreinte SHA-256",
        "On ne supprime rien",
      ],
      correct: 1,
    },
    {
      question: "Quel prestataire IA présente un risque de transfert nul ?",
      options: ["Claude (US)", "Qwen (Chine)", "Mistral (France, UE)"],
      correct: 2,
    },
    {
      question: "Le transfert vers Qwen (Chine) est encadré par :",
      options: [
        "Rien de particulier",
        "SCC + anonymisation Zéro-Knowledge",
        "Le DPF uniquement",
      ],
      correct: 1,
    },
    {
      question: "Que doit faire l'utilisateur en cas d'incident de données ?",
      options: ["Le signaler au DPO", "Ne rien faire", "Le publier sur le dossier"],
      correct: 0,
    },
  ],
};

const HITL: ModuleContenu = {
  id: "hitl",
  titre: "Module 2 — IA gouvernée & HITL",
  objectifs: [
    "Comprendre l'architecture IA Tiered et la surveillance humaine (Art. 14).",
  ],
  points: [
    "Tier 1 : validation humaine toujours ; Tier 2 : si confiance < 0,70 ; Tier 3 : auto > 0,95.",
    "Anti-fatigue : une validation en moins de 10 s est bloquée.",
    "Justification obligatoire sur toute modification d'une sortie IA.",
  ],
  scoreMax: 15,
  seuil: 12,
  qcm: [
    {
      question: "Pour le Tier 1 (stratégique), la validation humaine est :",
      options: ["Optionnelle", "Toujours requise", "Jamais requise"],
      correct: 1,
    },
    {
      question: "Une validation effectuée en moins de 10 secondes est :",
      options: ["Acceptée", "Bloquée (anti-fatigue)", "Ignorée"],
      correct: 1,
    },
    {
      question: "Au Tier 3, l'IA agit automatiquement si la confiance est :",
      options: ["> 0,95", "< 0,50", "= 0"],
      correct: 0,
    },
    {
      question: "Modifier une sortie IA impose :",
      options: ["Rien", "Une justification obligatoire", "L'accord du client"],
      correct: 1,
    },
    {
      question: "Le Tier 2 (médiation) requiert validation si confiance :",
      options: ["< 0,70", "> 0,99", "jamais"],
      correct: 0,
    },
  ],
};

const ETHIQUE: ModuleContenu = {
  id: "ethique",
  titre: "Module 4 — Séparation des rôles & éthique",
  objectifs: ["Respecter strictement les prérogatives de chacun."],
  points: [
    "L'avocat n'accède jamais à SentenceCompanion / aux brouillons de sentence.",
    "L'arbitre n'émet jamais de correspondance protocolaire avocat-avocat.",
    "La décision juridictionnelle ne se délègue pas à l'IA.",
  ],
  scoreMax: 10,
  seuil: 9,
  qcm: [
    {
      question: "Les brouillons de sentence relèvent :",
      options: [
        "Du secret du délibéré (arbitre)",
        "De l'espace avocat",
        "Du portail client",
      ],
      correct: 0,
    },
    {
      question: "La décision finale d'une sentence est prise par :",
      options: ["L'IA", "L'arbitre (humain)", "Le greffier"],
      correct: 1,
    },
    {
      question: "Émettre un message en tant qu'arbitre (par un avocat) est :",
      options: ["Autorisé", "Interdit (impersonation)", "Toléré"],
      correct: 1,
    },
    {
      question: "Qui peut modifier le statut d'un dossier ?",
      options: ["L'avocat", "L'arbitre / l'admin", "N'importe qui"],
      correct: 1,
    },
    {
      question: "L'IA dans le processus juridictionnel est :",
      options: ["Décisionnaire", "Un outil d'aide soumis à validation", "Autonome"],
      correct: 1,
    },
  ],
};

const AI_ACT: ModuleContenu = {
  id: "ai_act",
  titre: "Module 5 — Conformité AI Act & transparence",
  objectifs: ["Comprendre le statut haut risque et le registre des risques (Art. 9)."],
  points: [
    "Système à haut risque (Annexe III, pt. 8).",
    "Registre des risques tenu à jour ; signalement des dysfonctionnements.",
    "Réflexe : signaler toute suggestion absurde ou référence abrogée.",
  ],
  scoreMax: 10,
  seuil: 8,
  qcm: [
    {
      question: "Le système est classé :",
      options: ["Risque minimal", "Haut risque (Annexe III)", "Interdit"],
      correct: 1,
    },
    {
      question: "Face à une référence à une loi abrogée :",
      options: ["L'utiliser", "La signaler et vérifier", "L'ignorer"],
      correct: 1,
    },
    {
      question: "Le registre des risques relève de :",
      options: ["AI Act Art. 9", "Rien", "Une option commerciale"],
      correct: 0,
    },
    {
      question: "Qui supervise le registre des risques ?",
      options: ["Le client", "Le DPO", "Personne"],
      correct: 1,
    },
    {
      question: "Une suggestion IA absurde doit être :",
      options: ["Validée vite", "Signalée", "Transmise au confrère"],
      correct: 1,
    },
  ],
};

// --- Modules spécifiques AVOCAT --------------------------------------------
const COCKPIT_AVOCAT: ModuleContenu = {
  id: "cockpit",
  titre: "Module 3 — Cockpit Avocat : fonctionnalités",
  objectifs: [
    "Maîtriser conclusions, pièces, correspondance, audiences.",
    "Connaître les délais OHADA et la vérification CCJA.",
  ],
  points: [
    "Délai de sentence : 90 jours (Art. 20 AUA), prorogeable de 30 j.",
    "Référence CCJA : toujours vérifier la source (anti-hallucination).",
    "Correspondance protocolaire : 16 types canoniques, visibilité/statut.",
  ],
  scoreMax: null,
  seuil: null,
  qcm: [],
};

const CAS_PRATIQUE_AVOCAT: ModuleContenu = {
  id: "cas_pratique",
  titre: "Module 6 — Cas pratique final (avocat)",
  objectifs: ["Mettre en œuvre l'ensemble des compétences du conseil."],
  points: [
    "Rédiger avec assistance IA + validation humaine.",
    "Déposer une pièce, envoyer un message protocolaire.",
    "Vérifier une référence CCJA, répondre à une demande d'effacement.",
  ],
  scoreMax: 100,
  seuil: 80,
  qcm: [
    {
      question: "Avant d'insérer une référence CCJA suggérée par l'IA :",
      options: [
        "On la vérifie (date, numéro, statut)",
        "On l'insère directement",
        "On demande à l'arbitre",
      ],
      correct: 0,
    },
    {
      question: "Le client demande l'effacement d'une pièce. Vous expliquez :",
      options: [
        "Que c'est impossible",
        "Que le contenu est supprimé, l'empreinte (preuve) conservée",
        "Que tout est effacé y compris la preuve",
      ],
      correct: 1,
    },
    {
      question: "Une conclusion rédigée par l'IA est :",
      options: [
        "Déposée telle quelle",
        "Validée par l'avocat avant dépôt",
        "Validée par l'arbitre",
      ],
      correct: 1,
    },
    {
      question: "Le délai de sentence OHADA est de :",
      options: ["30 jours", "90 jours (prorogeable 30 j)", "1 an"],
      correct: 1,
    },
    {
      question: "Un message « demande de récusation » est :",
      options: [
        "Inter-avocats seulement",
        "Requis tribunal (l'arbitre doit statuer)",
        "Confidentiel",
      ],
      correct: 1,
    },
  ],
};

// --- Modules spécifiques ARBITRE -------------------------------------------
const DELIBERATION: ModuleContenu = {
  id: "deliberation",
  titre: "Module 3 — Délibéré & SentenceCompanion",
  objectifs: [
    "Maîtriser l'aide à la décision (SentenceCompanion) dans le respect du délibéré.",
    "Valider la sentence en HITL ; ancrer la preuve.",
  ],
  points: [
    "Le brouillon de sentence relève du secret du délibéré (jamais visible des parties).",
    "La sentence est validée humainement (HITL award) ; l'IA n'arrête rien.",
    "Délai de sentence 90 j (Art. 20 AUA) ; ancrage probatoire (TSA + blockchain).",
  ],
  scoreMax: null,
  seuil: null,
  qcm: [],
};

const CAS_PRATIQUE_ARBITRE: ModuleContenu = {
  id: "cas_pratique_arbitre",
  titre: "Module 6 — Cas pratique final (arbitre)",
  objectifs: ["Mettre en œuvre les compétences juridictionnelles."],
  points: [
    "Rédiger une sentence assistée puis la valider en HITL.",
    "Respecter le délai de 90 j et le secret du délibéré.",
    "Ancrer la preuve ; vérifier les références jurisprudentielles.",
  ],
  scoreMax: 100,
  seuil: 80,
  qcm: [
    {
      question: "Un brouillon de sentence est visible :",
      options: [
        "Des parties",
        "Du seul tribunal (secret du délibéré)",
        "Du public",
      ],
      correct: 1,
    },
    {
      question: "La validation finale de la sentence (HITL award) est faite par :",
      options: ["L'IA automatiquement", "L'arbitre (humain)", "Le greffier seul"],
      correct: 1,
    },
    {
      question: "Le délai pour rendre la sentence (OHADA) est de :",
      options: ["90 jours, prorogeable de 30 j", "6 mois", "Illimité"],
      correct: 0,
    },
    {
      question: "L'ancrage de la preuve repose sur :",
      options: ["Une simple copie", "Horodatage TSA + blockchain/Merkle", "Rien"],
      correct: 1,
    },
    {
      question: "Une référence CCJA suggérée par l'IA doit être :",
      options: ["Citée telle quelle", "Vérifiée (statut en vigueur)", "Ignorée"],
      correct: 1,
    },
  ],
};

export const CURRICULA_CONTENU: Record<string, ModuleContenu[]> = {
  AVOCAT: [ONBOARDING, RGPD, HITL, COCKPIT_AVOCAT, ETHIQUE, AI_ACT, CAS_PRATIQUE_AVOCAT],
  ARBITRE: [ONBOARDING, RGPD, HITL, DELIBERATION, ETHIQUE, AI_ACT, CAS_PRATIQUE_ARBITRE],
};

// Calcule le score d'un QCM (bonnes réponses × scoreMax/5).
export function calculerScore(module: ModuleContenu, reponses: number[]): number {
  if (module.scoreMax === null || module.qcm.length === 0) return 0;
  const bonnes = module.qcm.reduce(
    (n, q, i) => (reponses[i] === q.correct ? n + 1 : n),
    0,
  );
  return Math.round((bonnes / module.qcm.length) * module.scoreMax);
}
